<template>
    <section class="phone-number">
        <div class="widget__fields">
            <div class="label label--country">
                {{ $t('registerMobileNumber.country') }}:

                <multiselect id="input-country"
                    v-model="form.countryCode"
                    :options="countriesFormattedList"
                    :disabled="disabled"
                    :allow-empty="false"
                    :show-labels="false"
                    track-by="label"
                    label="label"
                    :placeholder="$t('registerMobileNumber.country')"
                    :class="['label__input', { 'label__input--active': form.countryCode }]"
                    @search-change="onSearchChange"
                >
                    <template slot="singleLabel"
                        slot-scope="props"
                    >
                        {{ props.option.code ? props.option.code : form.countryCode.internationalCallingCode }}
                    </template>

                    <template slot="option"
                        slot-scope="props"
                    >
                        <span :id="`country-code-${props.option.iso2}`">{{ props.option.label }}</span>
                    </template>

                    <span slot="noResult">{{ $t('registerMobileNumber.noResultsCountries') }}</span>
                </multiselect>
            </div>

            <label class="label label--phone">
                {{ $t('registerMobileNumber.phoneNumber') }}:

                <input id="input-phone"
                    v-model="form.phoneNumber"
                    type="text"
                    :disabled="disabled"
                    :placeholder="$t('registerMobileNumber.enterPhoneNumber')"
                    :class="['label__input', { 'label__input--active': form.phoneNumber }]"
                >

                <div v-if="showInvalidPhoneError"
                    class="label__error"
                >
                    {{ $t('registerMobileNumber.invalidPhoneNumber') }}
                </div>

                <div v-if="isBlockedCountry"
                    class="label__error"
                >
                    {{ $t('registerMobileNumber.blacklistedCountry') }}
                </div>
            </label>
        </div>

        <div class="label__hint">
            {{ $t('registerMobileNumber.phoneNumberInfo') }}
        </div>
    </section>
</template>

<script>
const CLOUDFLARE_LOCALE_PARAM = 'loc';
const CLOUDLARE_TRACE_URL = 'https://www.cloudflare.com/cdn-cgi/traces';

// 3rd party
import axios from 'axios';

// Store
import {
    mapGetters,
    mapActions,
} from 'vuex';
import localStoreTypes from '@webapp/store/types.js';

export default {
    name: 'phone-number',
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        showInvalidPhoneError: {
            type: Boolean,
            required: true,
        },
        isBlockedCountry: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            form: {
                countryCode: '',
                phoneNumber: '',
            },
            filteredCountries: [],
        };
    },
    computed: {
        ...mapGetters([
            'isPhoneNumberValid',
            'isPhoneNumberAvailable',
        ]),

        ...mapGetters('countries', ['countriesList']),

        countriesFormattedList() {
            return [...this.filteredCountries]
                .map((country) => ({
                    name: country.name,
                    iso2: country.iso2,
                    code: country.internationalCallingCode,
                    label: `${country.internationalCallingCode} ${country.display} (${country.iso2})`,
                }));
        },
    },
    watch: {
        'form.countryCode': 'updatePhoneNumber',
        'form.phoneNumber': 'updatePhoneNumber',
    },
    async created() {
        try {
            const country = await this.getCountryFromIP();

            this.form.countryCode = this.countriesList.find((item) => item.iso2 === country);

            if (this.form.countryCode) {
                this.$nextTick(() => {
                    document.getElementById('input-phone').focus();
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.filteredCountries = [...this.countriesList];
        }

        this.checkForPrepopulatedUserInfo();
    },
    methods: {
        ...mapActions({
            setRegistration: localStoreTypes.SET_REGISTRATION,
        }),

        onSearchChange(searchParam) {
            if (!searchParam) {
                this.filteredCountries = [...this.countriesList];

                return;
            }

            const searchedText = searchParam.replace('+', '')
                .toString()
                .toLowerCase();

            if (isNaN(searchParam)) {
                this.filteredCountries = this.countriesList
                    .filter((country) => country.translations?.[0]?.value.toLowerCase().startsWith(searchedText));
            } else {
                this.filteredCountries = this.countriesList
                    .filter((country) => country.internationalCallingCode.replace('+', '').startsWith(searchedText));
            }
        },

        updatePhoneNumber() {
            this.setRegistration([
                {
                    property: 'countryCode',
                    value: this.form.countryCode ? this.form.countryCode.iso2 : '',
                },
                {
                    property: 'phoneNumber',
                    value: this.form.phoneNumber,
                },
            ]);
        },

        async getCountryFromIP() {
            const response = await axios.get(CLOUDLARE_TRACE_URL);
            const responseToObject = response.data.split('\n').reduce((obj, item) => {
                const [key, value] = item.split('=');

                return {
                    ...obj,
                    [key]: value,
                };
            }, {});

            return responseToObject[CLOUDFLARE_LOCALE_PARAM];
        },

        checkForPrepopulatedUserInfo() {
            if (window.location.search) {
                const userData = new URLSearchParams(window.location.search).get('userInfo') || '';

                if (userData) {
                    const decodedUserData = JSON.parse(decodeURIComponent(atob(userData)));

                    this.form.countryCode = this.countriesList.find((item) => item.internationalCallingCode ===
                        decodedUserData.countryCode?.replace(/\s/g, '+'));
                    this.form.phoneNumber = decodedUserData.phoneNumber;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.label {
    &--country {
        width: 22%;
        margin-right: 2.5rem;
        color: $blue-charcoal-color;
    }

    &--phone {
        flex: 1;
        color: $blue-charcoal-color;
    }
}
</style>